import { useApi } from "@shopexperts/shared";
import { useTokenStorage } from "@/app/composables/useTokenStorage";
import { addConvexBreadcrumb, captureConvexError } from "@/v2Projects/telemetry";

const SKEW_SECONDS = 30;
const CONVEX_JWT_STORAGE_KEY = "CONVEX_JWT_CACHE";

let cachedToken: string | null = null;
let expiresAtMs = 0;
let cachedAccessToken: string | null = null;
let inFlight: Promise<string | null> | null = null;
let retriedForBadIssuer = false;

interface PersistedConvexJwtCache {
  token: string;
  expiresAtMs: number;
  accessToken: string;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payloadSegment] = token.split(".");
    if (!payloadSegment) {
      return null;
    }
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (error) {
    captureConvexError(error, {
      operation: "convex_auth.decode_jwt_payload_failed",
      level: "warning",
    });
    return null;
  }
}

function readPersistedConvexJwtCache(): PersistedConvexJwtCache | null {
  try {
    const raw = localStorage.getItem(CONVEX_JWT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedConvexJwtCache>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.expiresAtMs !== "number" ||
      typeof parsed.accessToken !== "string"
    ) {
      localStorage.removeItem(CONVEX_JWT_STORAGE_KEY);
      addConvexBreadcrumb({
        operation: "convex_auth.persisted_cache_invalid_shape",
        level: "warning",
        auth: { hasConvexToken: typeof parsed.token === "string" },
      });
      return null;
    }
    return {
      token: parsed.token,
      expiresAtMs: parsed.expiresAtMs,
      accessToken: parsed.accessToken,
    };
  } catch (error) {
    localStorage.removeItem(CONVEX_JWT_STORAGE_KEY);
    captureConvexError(error, {
      operation: "convex_auth.persisted_cache_parse_failed",
      level: "warning",
    });
    return null;
  }
}

function persistConvexJwtCache(cache: PersistedConvexJwtCache) {
  localStorage.setItem(CONVEX_JWT_STORAGE_KEY, JSON.stringify(cache));
}

function clearPersistedConvexJwtCache() {
  localStorage.removeItem(CONVEX_JWT_STORAGE_KEY);
}

function clearInMemoryConvexJwtCache() {
  cachedToken = null;
  expiresAtMs = 0;
  cachedAccessToken = null;
}

export function clearStoredConvexTokenCache() {
  clearInMemoryConvexJwtCache();
  clearPersistedConvexJwtCache();
}

function hasLocalhostIssuer(token: string): boolean {
  const payload = decodeJwtPayload(token);
  return typeof payload?.iss === "string" && payload.iss.includes("localhost");
}

function hydrateConvexJwtCache(currentAccessToken: string) {
  if (cachedToken && cachedAccessToken === currentAccessToken) return;

  const persisted = readPersistedConvexJwtCache();
  if (!persisted) return;

  const now = Date.now();
  if (persisted.accessToken !== currentAccessToken || now >= persisted.expiresAtMs) {
    clearPersistedConvexJwtCache();
    return;
  }

  if (hasLocalhostIssuer(persisted.token)) {
    captureConvexError(new Error("Persisted Convex JWT has localhost issuer"), {
      operation: "convex_auth.persisted_localhost_issuer",
      level: "warning",
      auth: { hasConvexToken: true },
    });
    clearPersistedConvexJwtCache();
    return;
  }

  cachedToken = persisted.token;
  expiresAtMs = persisted.expiresAtMs;
  cachedAccessToken = persisted.accessToken;
}

export function useConvexAuthToken() {
  const api = useApi();
  const tokenStorage = useTokenStorage();

  async function fetchConvexToken(forceRefresh = false): Promise<string | null> {
    const currentAccessToken = tokenStorage.getToken();
    if (!currentAccessToken) {
      clearStoredConvexTokenCache();
      addConvexBreadcrumb({
        operation: "convex_auth.missing_access_token",
        level: "warning",
        auth: { hasAccessToken: false },
      });
      return null;
    }

    hydrateConvexJwtCache(currentAccessToken);

    if (cachedAccessToken && cachedAccessToken !== currentAccessToken) {
      forceRefresh = true;
      clearStoredConvexTokenCache();
    }

    const now = Date.now();
    if (!forceRefresh && cachedToken && now < expiresAtMs) {
      if (hasLocalhostIssuer(cachedToken)) {
        captureConvexError(new Error("Cached Convex JWT has localhost issuer"), {
          operation: "convex_auth.cached_localhost_issuer",
          level: "warning",
          auth: { hasAccessToken: true, hasConvexToken: true },
        });
        clearStoredConvexTokenCache();
      } else {
        return cachedToken;
      }
    }

    if (inFlight) {
      return inFlight;
    }

    inFlight = (async () => {
      let response = await api.public.auth.jwtToken();
      let payload = decodeJwtPayload(response.token);

      if (typeof payload?.iss === "string" && payload.iss.includes("localhost") && !retriedForBadIssuer) {
        captureConvexError(new Error("Convex JWT issuer is localhost; retrying once"), {
          operation: "convex_auth.localhost_issuer_retry",
          level: "warning",
          auth: { hasAccessToken: true, hasConvexToken: true, forceRefreshToken: forceRefresh },
          extra: { issuer: payload.iss },
        });
        retriedForBadIssuer = true;
        response = await api.public.auth.jwtToken();
        payload = decodeJwtPayload(response.token);
      }

      cachedToken = response.token;
      cachedAccessToken = currentAccessToken;

      if (typeof payload?.iss === "string" && payload.iss.includes("localhost")) {
        captureConvexError(new Error("Convex JWT issuer is localhost after retry"), {
          operation: "convex_auth.localhost_issuer_unrecovered",
          level: "error",
          auth: { hasAccessToken: true, hasConvexToken: true, forceRefreshToken: forceRefresh },
          extra: { issuer: payload.iss },
        });
      } else {
        retriedForBadIssuer = false;
      }

      addConvexBreadcrumb({
        operation: "convex_auth.jwt_received",
        level: "info",
        auth: { hasAccessToken: true, hasConvexToken: true },
        extra: {
          applicationId: response.application_id,
          expiresIn: response.expires_in,
          issuer: payload?.iss,
          audience: payload?.aud,
          payloadApplicationId: payload?.application_id,
          subject: payload?.sub,
        },
      });

      const ttlMs = Math.max(0, (response.expires_in - SKEW_SECONDS) * 1000);
      expiresAtMs = Date.now() + ttlMs;
      persistConvexJwtCache({
        token: cachedToken,
        expiresAtMs,
        accessToken: currentAccessToken,
      });
      return cachedToken;
    })()
      .catch((error) => {
        clearStoredConvexTokenCache();
        captureConvexError(error, {
          operation: "convex_auth.jwt_fetch_failed",
          level: "error",
          auth: { hasAccessToken: true, forceRefreshToken: forceRefresh },
        });
        return null;
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  function clearConvexTokenCache() {
    clearStoredConvexTokenCache();
  }

  return {
    fetchConvexToken,
    clearConvexTokenCache,
  };
}
